/**
 * React context for strategy questionnaire — fetches API payload and exposes session hook.
 *
 * Belongs here: query setup, context provider, useStrategyQuestionnaireContext.
 * Does not belong here: page handlers, Find-my-strategy, or review out-of-sync autosubmit.
 *
 * `rawQuestionnaireData` = full API response. `questionnaireData` may be single-stage filtered
 * when editing from review (see useStrategyQuestionnaire).
 */
import { createContext, ReactNode, useContext } from 'react'
import type {
  Question,
  StrategyQuestionnairePayload,
  StrategyQuestionnaireAnswersMap,
  StrategyQuestionnaireStage
} from '../../shared/types/questionnaire.types'
import type { AllocationRiskProfile } from '../../types/allocation.types'
import type { SlideDirection } from '../../shared/transitions/DirectionalSlideSwap'
import type { ExpertReviewCameFrom } from '../../navigation/expertReviewNavigation'
import type { SubmitStrategyAnswersVars } from '../../api/useSubmitStrategyAnswersMutation'
import {
  useStrategyQuestionnaire,
  type StrategyAnswerResult,
  type StrategyLeave,
  type StrategySessionMode
} from './useStrategyQuestionnaire'
import { useStrategyQuestionnaireQuery } from '../../api/useStrategyQuestionnaireQuery'
import { useExpertReviewStatusContext } from '../../providers/ExpertReviewStatusContext'

export interface StrategyQuestionnaireContextType {
  questionnaireData: StrategyQuestionnairePayload | undefined
  rawQuestionnaireData: StrategyQuestionnairePayload | undefined
  isQuestionnaireLoading: boolean
  isQuestionnaireError: boolean
  refetchQuestionnaire: () => void
  draftAnswers: StrategyQuestionnaireAnswersMap
  committedAnswers: StrategyQuestionnaireAnswersMap
  committedAnswersVersion: number
  lastSubmittedAnswersVersion: number
  isAnswersVersionOutOfSync: boolean
  /** POST only — Find-my-strategy when out of sync (status after compute). */
  submitAnswers: (vars: SubmitStrategyAnswersVars) => Promise<void>
  /** POST then /status refresh — review autosubmit, hub SIP leave. */
  submitAnswersAndStatusRefresh: (
    vars: SubmitStrategyAnswersVars,
    options?: { silent?: boolean }
  ) => Promise<void>
  /** Hub: after full review once (or seeded done), open review instead of questionnaire. */
  hasReachedReviewOnce: boolean
  markReachedReview: () => void
  /** Discard draft and leave answering (pop questionnaire route). */
  cancelAnswering: () => void
  /** Clear one stage into draft and open edit route (review / normal — SIP included). */
  beginEditOne: (stageId: string, options?: { cameFrom?: ExpertReviewCameFrom }) => void
  /** Discard draft only and reopen full questionnaire (committed stays until AllDone). */
  beginEditAll: () => void
  /** Hub Monthly SIP card only — sets HubSip mode, clears SIP stage into draft, opens edit. */
  openHubSipEdit: () => void
  /** Discard uncommitted draft, reset cursor + session mode (hub / menu leave). */
  clearSession: () => void
  /** Real answer tap — switch on result.type; on allDone call leaveQuestionnaire(leave). */
  answerQuestion: (questionId: string, value: string | number) => StrategyAnswerResult
  /** Selection highlight only — writes draftAnswers, does not move forward. */
  updateDraftAnswer: (questionId: string, value: string | number) => void
  clearAnswer: (questionId: string) => void
  /** Navigate / POST using Leave from allDone. Always pass leave.answers for hub_sip. */
  leaveQuestionnaire: (leave: StrategyLeave) => void | Promise<void>
  stageIndex: number
  questionIndex: number
  riskProfile: AllocationRiskProfile | null
  setRiskProfileFromStatus: (riskProfile: AllocationRiskProfile | null) => void
  sessionMode: StrategySessionMode
  stage: StrategyQuestionnaireStage | undefined
  visibleQuestions: Question[]
  currentQuestion: Question | undefined
  progressInStage: number
  isLastStage: boolean
  slideDirection: SlideDirection
  goToQuestion: (params: {
    nextStageIndex: number
    nextQuestionIndex: number
    direction: SlideDirection
  }) => void
  /** After stage animation (or immediately on edit): open next stage at question 0. */
  goNextStage: (nextStageIndex: number) => void
  startTransition: (direction: SlideDirection) => void
}

const StrategyQuestionnaireContext = createContext<StrategyQuestionnaireContextType | undefined>(
  undefined
)

interface StrategyQuestionnaireProviderProps {
  children: ReactNode
  stageIdFromPathParam?: string
}

/** Fetches questionnaire payload once; session hook lives for the provider subtree. */
export function StrategyQuestionnaireProvider({
  children,
  stageIdFromPathParam
}: StrategyQuestionnaireProviderProps) {
  const {
    data: rawQuestionnaireData,
    isLoading: isQuestionnaireQueryLoading,
    isError: isQuestionnaireError,
    refetch: refetchQuestionnaire
  } = useStrategyQuestionnaireQuery()

  const { statusData, isStatusLoading, isStatusError } = useExpertReviewStatusContext()

  // Pages that need answers treat status settle like questionnaire loading
  const isQuestionnaireLoading = isQuestionnaireQueryLoading || isStatusLoading

  const flowState = useStrategyQuestionnaire(stageIdFromPathParam, rawQuestionnaireData, {
    isStatusLoading,
    isStatusError,
    questionnaireStatus: statusData?.strategyQuestionnaire?.status
  })

  return (
    <StrategyQuestionnaireContext.Provider
      value={{
        ...flowState,
        rawQuestionnaireData,
        isQuestionnaireLoading,
        isQuestionnaireError,
        refetchQuestionnaire
      }}
    >
      {children}
    </StrategyQuestionnaireContext.Provider>
  )
}

/** Session + query facade — must render under StrategyQuestionnaireProvider. */
export function useStrategyQuestionnaireContext() {
  const context = useContext(StrategyQuestionnaireContext)
  if (!context) {
    throw new Error('useStrategyQuestionnaireContext must be used within StrategyQuestionnaireProvider')
  }
  return context
}
